import { FC, useEffect, useState } from 'react';
import { Button, Space, Textarea, Text } from '@g2u/ui-kit';
import { format, parse, parseISO, isValid } from 'date-fns';
import { Modal, Input, Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import he from 'he';

import { SummarySection } from './summary-section';
import { extractServiceDetails, mapOperatingSystemsToLabels } from 'helpers/extractServiceDetails';
import { isTimeExpired, parseErrors, Toast, useJob } from 'helpers';
import { getServiceSelection, putServiceSelection, putTechNotes } from 'queries';
import { PopConfirm } from 'components';
import { Condition, JobServiceTypeLabel, JsonDataItem, PricingCategoryLabel, ServiceType } from 'types';
import { useServiceCancellationDetails } from '../hooks';
import styles from './service-summary.module.scss';

const serviceTypeTitles = {
  [ServiceType.OnSite]: 'On-site',
  [ServiceType.Remote]: 'Remote',
  [ServiceType.GeekCover]: 'GeekCover Remote',
};

enum JobSourceLabel {
  bmsv3 = 'BMS Plus',
  legacy = 'BMS Core',
}

type Props = {
  jobId?: string;
  isLocked: boolean;
};

export const ServiceSummary: FC<Props> = (props) => {
  const { jobId, isLocked } = props;
  const [serviceList, setServiceList] = useState<JsonDataItem | undefined>(undefined);
  const [OSLabels, setOSLabels] = useState<Record<string, string>>({});
  const { job, serviceSelection, refetchService, isLoading } = useJob(jobId);
  const [serviceDescriptionForm] = Form.useForm();
  const [techNoteForm] = Form.useForm();
  const serviceDesscription = extractServiceDetails(job?.service_description).problem;
  const { cancellationDetails } = useServiceCancellationDetails(job?.attributes);
  const technicianNote = job?.products[0]?.service_technicians[0]?.technician_note;
  const [isFormValid, setIsFormValid] = useState<boolean>(true);
  const isClosed = ['cancelled', 'completed'].includes(job?.status ?? '');
  const isJobPartsOnly = job?.service_type_ID?.toString() === ('4' as ServiceType);
  const requestedAppointment = job?.attributes.find(
    (item: JsonDataItem) => item.key === 'requested_appointment',
  )?.value;
  const navigate = useNavigate();

  const handleError = (error: JsonDataItem) => {
    const errors = parseErrors(error).join(', ');
    Toast.show({ description: errors, type: Toast.Type.Error });
  };

  useQuery(['service-selection'], getServiceSelection, {
    onSuccess: (data: JsonDataItem) => {
      setServiceList(data);
    },
    onError: handleError,
  });

  const updateJobDescription = useMutation(putServiceSelection, {
    onSuccess: () => {
      refetchService();
    },
    onError: handleError,
  });

  const updateTechNotes = useMutation(putTechNotes, {
    onSuccess: () => {
      refetchService();
    },
    onError: handleError,
  });

  useEffect(() => {
    if (serviceList) {
      const osOptions = serviceList.child
        .filter((child: any) => child.name === 'os')
        .flatMap((child: any) => child.options);

      const osLabels = osOptions.reduce(
        (acc: Record<string, string>, { value, label }: { value: string; label: string }) => {
          acc[value] = label;
          return acc;
        },
        {},
      );

      setOSLabels(osLabels);
    }
  }, [serviceList, serviceSelection]);

  const [isServiceDescModalOpen, setIsServiceDescModalOpen] = useState<boolean>(false);
  const [isTechNoteModalOpen, setIsTechNoteModalOpen] = useState<boolean>(false);

  const showServiceDescModal = () => {
    setIsServiceDescModalOpen(true);
  };

  const showTechNoteModal = () => {
    setIsTechNoteModalOpen(true);
  };

  const handleUpdateServiceDesc = (formData: JsonDataItem | null) => {
    const newServiceDescription = formData?.problem;
    if (newServiceDescription !== serviceDesscription) {
      updateJobDescription.mutate({
        selectionId: serviceSelection?.ID || '',
        description: newServiceDescription,
        products: serviceSelection?.product_selection_raw?.products || [],
        os: serviceSelection?.product_selection_raw?.os || [],
        devices: serviceSelection?.product_selection_raw?.devices || [],
        additionalProblem: serviceDescriptionForm.getFieldValue('additional_problem') || '',
        noAutoAppend: true,
      });
    }
    setIsServiceDescModalOpen(false);
  };

  const handleUpdateTechnicianNotes = (formData: JsonDataItem | null) => {
    const newNote = formData?.technician_note;
    if (newNote !== technicianNote) {
      jobId &&
        updateTechNotes.mutate({
          technicianNote: newNote,
          jobId: jobId,
        });
    }
    setIsTechNoteModalOpen(false);
  };

  const handleCancel = () => {
    if (isServiceDescModalOpen) {
      serviceDescriptionForm.resetFields();
      setIsServiceDescModalOpen(false);
    }
    if (isTechNoteModalOpen) {
      techNoteForm.resetFields();
      setIsTechNoteModalOpen(false);
    }
  };

  const handleValuesChange = (value: any) => {
    const problem = value.problem;
    setIsFormValid(!problem || problem.trim() === '');
  };

  return (
    <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large}>
      <SummarySection
        isLoading={isLoading}
        title={'Job Details'}
        details={[
          {
            key: 'Appointment Time',
            value: (() => {
              if (((!job || !job.products[0]) && !isClosed) || (!requestedAppointment && job?.status === 'cancelled'))
                return 'N/A';

              const parsedDate = parse(
                requestedAppointment || job?.products[0]?.service_technicians?.[0]?.appointment || '',
                'yyyy-MM-dd HH:mm:ss',
                new Date(),
              );
              return isValid(parsedDate) ? format(parsedDate, 'h:mmaaa dd-MM-yyyy') : '';
            })(),
          },
          { key: 'Address', value: job?.customer.address.full_address },
          { key: 'Customer', value: he.decode(job?.customer.name.full_name || '') },
          { key: 'Technician', value: he.decode(job?.products[0]?.service_technicians?.[0]?.technician.name || 'N/A') },
          { key: 'Job ID', value: jobId },
          {
            key: 'Pricing Category',
            value: PricingCategoryLabel[job?.customer.customer_type as keyof typeof PricingCategoryLabel],
          },
          { key: 'Status', value: job?.status ? job.status.charAt(0).toUpperCase() + job.status.slice(1) : '' },
          {
            key: 'Booking Source',
            value: job?.user_ID === -2 ? 'Online Booking' : JobSourceLabel[job?.source as keyof typeof JobSourceLabel],
          },
          {
            key: 'Created Time',
            value: (() => {
              if (!job) return '';
              const parsedDate = parseISO(job.created_at);
              return isValid(parsedDate) ? format(parsedDate, 'h:mmaaa dd-MM-yyyy') : '';
            })(),
          },
          {
            key: 'Created By',
            value: he.decode(job?.created_by.name || ''),
          },
          {
            key: 'Job Type',
            value: serviceTypeTitles[job?.service_type_ID?.toString() as ServiceType],
          },
          {
            key: 'Service Type',
            value:
              JobServiceTypeLabel[
                serviceSelection?.service_attributes?.job_service_type as keyof typeof JobServiceTypeLabel
              ],
          },
          { key: 'Service', value: extractServiceDetails(job?.service_description).service },
          { key: 'Device(s)', value: extractServiceDetails(job?.service_description).deviceTypes.join(', ') },
          {
            key: 'Operating System(s)',
            value: mapOperatingSystemsToLabels(
              OSLabels,
              extractServiceDetails(job?.service_description).operatingSystems,
            ).join(', '),
          },
          { key: 'Job Description', value: serviceDesscription },
          {
            key: 'Additional Problem',
            value: extractServiceDetails(job?.service_description).additionalProblem,
          },
        ]}
        actionButtons={[
          <Button.$
            key="update-technician-notes"
            dataTestId="update-technician-notes-btn"
            onClick={showTechNoteModal}
            disabled={isLocked}
          >
            Update Tech Note
          </Button.$>,
          <>
            {!isEmpty(serviceSelection) && (
              <Button.$
                key="update-service-description"
                dataTestId="update-service-description-btn"
                onClick={showServiceDescModal}
                disabled={isLocked}
              >
                Update Job Description
              </Button.$>
            )}
          </>,
          <>
            {!isClosed && !isJobPartsOnly && (
              <Button.$
                key="reschedule-job"
                dataTestId="reschedule-job-btn"
                onClick={() => (window.location.href = `/job/reschedule/${jobId}/select-reason`)}
                disabled={job?.status === 'completed' || isLocked}
              >
                Reschedule Job
              </Button.$>
            )}
          </>,
          <>
            {!isClosed && (
              <>
                {isJobPartsOnly ? (
                  <Button.$
                    onClick={() =>
                      navigate(`/job/cancel/${job?.ID}`, {
                        state: { charges: Condition.No, returnTo: location.pathname },
                      })
                    }
                    dataTestId="cancel-job-btn"
                    disabled={isLocked}
                  >
                    Cancel Job
                  </Button.$>
                ) : (
                  <PopConfirm
                    key="cancel-job"
                    title="Cancel this job"
                    description={
                      <Space.$ direction={Space.Direction.Vertical}>
                        {isTimeExpired(
                          job?.products[0]?.service_technicians[0]?.appointment || '',
                          job?.customer.timezone,
                          24,
                        ) ? (
                          <>
                            <Text.$>
                              There is less than 24 hours remaining before the appointment time. A 1-hour service fee
                              will be charged to the customer and credited to their account.
                            </Text.$>
                            <Text.$>Do you want to continue?</Text.$>
                          </>
                        ) : (
                          <>
                            <Text.$>
                              There is more than 24 hours remaining before the appointment time. The customer can cancel
                              it without any charge.
                            </Text.$>
                            <Text.$>Do you want to proceed?</Text.$>
                          </>
                        )}
                      </Space.$>
                    }
                    actions={[
                      {
                        label: 'No',
                      },
                      ...(isTimeExpired(
                        job?.products[0]?.service_technicians[0]?.appointment || '',
                        job?.customer.timezone,
                        24,
                      )
                        ? [
                            {
                              label: 'Yes, but waive the fee',
                              action: () => {
                                navigate(`/job/cancel/${job?.ID}`, {
                                  state: { charges: Condition.No, returnTo: `/job/edit/${job?.ID}/summary` },
                                });
                                Toast.show({
                                  description: <>Cancellation charge has been waived for this job.</>,
                                  type: Toast.Type.Success,
                                });
                              },
                            },
                          ]
                        : []),
                      {
                        label: 'Yes',
                        action: () => {
                          navigate(`/job/cancel/${job?.ID}`, {
                            state: { charges: Condition.Yes, returnTo: `/job/edit/${job?.ID}/service-details` },
                          });
                        },
                        type: Button.Type.Primary,
                      },
                    ]}
                  >
                    <div>
                      <Button.$ dataTestId="cancel-job-btn" disabled={isLocked}>
                        Cancel Job
                      </Button.$>
                    </div>
                  </PopConfirm>
                )}
              </>
            )}
          </>,
        ]}
      />
      {/* TODO: Service Type is currently only available in Booking Sotre, in contexts like this, Service Types need to be available from */}

      <Modal
        title="Update Job Description"
        open={isServiceDescModalOpen}
        onOk={() => {
          serviceDescriptionForm.submit();
        }}
        okText="Save"
        okButtonProps={{ disabled: isFormValid }}
        onCancel={handleCancel}
        className={styles.summaryModal}
      >
        <Form
          form={serviceDescriptionForm}
          layout="vertical"
          initialValues={{
            problem: serviceDesscription,
          }}
          onValuesChange={handleValuesChange}
          onFinish={handleUpdateServiceDesc}
        >
          <Form.Item
            name="problem"
            rules={[
              { required: true },
              {
                validator: (_, value) =>
                  value && value.trim() !== ''
                    ? Promise.resolve()
                    : Promise.reject(new Error('Problem description cannot be empty')),
              },
            ]}
          >
            <Input.TextArea rows={6} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Update Note for Technician"
        open={isTechNoteModalOpen}
        onOk={() => {
          techNoteForm.submit();
        }}
        okText="Save"
        onCancel={handleCancel}
        className={styles.summaryModal}
      >
        <Form
          form={techNoteForm}
          layout="vertical"
          initialValues={{
            technician_note: technicianNote,
          }}
          onFinish={handleUpdateTechnicianNotes}
        >
          <Textarea.$ name="technician_note" rows={6} />
        </Form>
      </Modal>

      {cancellationDetails && (
        <SummarySection
          isDanger
          title={'Job Cancelled'}
          details={[
            { key: 'Cancelled On', value: cancellationDetails.date },
            { key: 'Cancelled By', value: cancellationDetails.user },
            { key: 'Cancellation Reason', value: cancellationDetails.reason },
            { key: 'Comments', value: cancellationDetails.comment },
          ]}
        />
      )}

      <SummarySection
        title={'Customer Details'}
        details={[
          { key: 'Customer ID', value: job?.customer_ID },
          { key: 'Name', value: he.decode(job?.customer.name.full_name || '') },
          { key: 'Mobile', value: !isEmpty(job?.customer.mobile) ? job?.customer.mobile : ' ' },
          { key: 'Home/Business Address', value: job?.customer.address.full_address },
        ]}
        actionButtons={[
          <Button.$ hidden key="quick-edit" dataTestId="quick-edit-btn">
            Quick Edit
          </Button.$>,
          <Button.$
            key="open-customer-file"
            dataTestId="open-customer-file-btn"
            onClick={() => (window.location.href = `/customer/edit/${job?.customer_ID}/details`)}
          >
            Open Customer File
          </Button.$>,
        ]}
      />
      <SummarySection
        title={'Technician Details'}
        details={[
          { key: 'Technician ID', value: job?.products[0]?.service_technicians[0]?.technician_ID },
          { key: 'Name', value: he.decode(job?.products[0]?.service_technicians[0]?.technician.name || 'N/A') },
          {
            key: 'Technician Confirmed',
            value: job?.products[0]?.service_technicians[0]?.technician_confirm ? 'Yes' : 'No',
          },
          { key: 'Note', value: technicianNote || 'N/A' },
        ]}
        actionButtons={[
          <Button.$ hidden key="add-note-for-technician" disabled dataTestId="add-note-for-technician-btn">
            Add Note for Technician
          </Button.$>,
        ]}
      />
      {job?.previous_service_description && (
        <SummarySection
          title={'Previous Job'}
          details={[
            {
              key: 'Job Description',
              value: extractServiceDetails(job?.previous_service_description).problem,
            },
          ]}
        />
      )}
    </Space.$>
  );
};
